import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Kanban, Calendar as CalendarIcon, Layers, Loader2, X, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem("sb_admin_token") || "";
const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

const VIEW_ICON = { kanban: Kanban, calendar: CalendarIcon };

const NewBoardModal = ({ open, onClose, onCreated }) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [view, setView] = useState("kanban");
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const submit = async () => {
    if (!name.trim()) return toast.error("Name required");
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/boards`,
        {
          name: name.trim(),
          description: description.trim(),
          view,
          accent: "#00f0ff",
          columns: view === "kanban"
            ? [
                { id: "new",  label: "New",  color: "#a78bfa", order: 0 },
                { id: "wip",  label: "In progress", color: "#f59e0b", order: 1 },
                { id: "done", label: "Done", color: "#22c55e", order: 2 },
              ]
            : [],
          sources: [
            { collection: "bookings", status_field: "status", status_map: {} },
          ],
        },
        { headers: authHeaders() },
      );
      toast.success(`Board "${data.name}" created.`);
      onCreated(data);
      setName(""); setDescription("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="new-board-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl bg-[#11091a] border border-white/15 p-6 relative"
        data-testid="new-board-modal"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/[0.06] border border-white/15 hover:bg-white/[0.12] flex items-center justify-center"
          aria-label="Close"
          data-testid="new-board-close"
        >
          <X size={16} />
        </button>
        <div className="font-display font-bold text-2xl mb-1">New board</div>
        <div className="text-[#a395c3] text-sm mb-6">Custom columns, drag-drop, all inside Command Center.</div>
        <label className="block text-[10px] uppercase tracking-[0.28em] text-white/50 mb-2">Name</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="new-board-name"
          placeholder="e.g. Client work Q1"
          className="w-full mb-4 px-4 py-3 rounded-xl bg-white/[0.04] border border-white/15 text-sm focus:outline-none focus:border-[#00f0ff]/50"
        />
        <label className="block text-[10px] uppercase tracking-[0.28em] text-white/50 mb-2">Description</label>
        <input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          data-testid="new-board-desc"
          placeholder="What this board tracks"
          className="w-full mb-4 px-4 py-3 rounded-xl bg-white/[0.04] border border-white/15 text-sm focus:outline-none focus:border-[#00f0ff]/50"
        />
        <label className="block text-[10px] uppercase tracking-[0.28em] text-white/50 mb-2">View</label>
        <div className="grid grid-cols-2 gap-2 mb-6">
          {["kanban", "calendar"].map((v) => (
            <button
              key={v}
              onClick={() => setView(v)}
              data-testid={`new-board-view-${v}`}
              className={`py-3 rounded-xl border text-sm font-medium inline-flex items-center justify-center gap-2 transition-colors ${
                view === v ? "bg-[#00f0ff]/10 border-[#00f0ff]/50 text-[#00f0ff]" : "bg-white/[0.04] border-white/15 text-white/60"
              }`}
            >
              {v === "kanban" ? <Kanban size={14} /> : <CalendarIcon size={14} />}
              {v[0].toUpperCase() + v.slice(1)}
            </button>
          ))}
        </div>
        <button
          onClick={submit}
          disabled={saving || !name.trim()}
          data-testid="new-board-submit"
          className="w-full py-3 rounded-full bg-[#00f0ff] text-[#05050a] font-semibold text-sm hover:bg-white transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          Create board
        </button>
      </div>
    </div>
  );
};

export default function AdminBoards() {
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/boards`, { headers: authHeaders() });
      setBoards(data.boards || []);
    } catch (e) {
      toast.error("Failed to load boards");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const del = async (slug, name) => {
    if (!window.confirm(`Delete "${name}"? Cards stay — only the board goes.`)) return;
    try {
      await axios.delete(`${API}/admin/boards/${slug}`, { headers: authHeaders() });
      toast.success("Board deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="relative min-h-screen bg-[#05050a] text-white overflow-hidden">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute -top-40 left-1/4 w-[520px] h-[520px] rounded-full bg-[#00f0ff]/8 blur-[120px]" />
        <div className="absolute bottom-[-15%] right-[10%] w-[420px] h-[420px] rounded-full bg-[#a78bfa]/8 blur-[140px]" />
      </div>

      <div className="relative max-w-[1300px] mx-auto px-5 md:px-10 py-8 md:py-12">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-10">
          <div>
            <Link to="/admin" className="text-white/60 hover:text-white text-sm inline-flex items-center gap-2 mb-3" data-testid="boards-back">
              <ArrowLeft size={14} /> Command Center
            </Link>
            <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">
              CRM <span className="text-[#00f0ff]">boards.</span>
            </h1>
            <p className="text-[#a395c3] text-sm mt-2">
              Kanban + Calendar views · custom columns · card labels · everything native.
            </p>
          </div>
          <div className="flex items-center gap-2 self-start">
            <button
              onClick={() => setShowNew(true)}
              data-testid="new-board-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#00f0ff] text-[#05050a] font-semibold text-sm tracking-tight shadow-lg shadow-[#00f0ff]/20 hover:bg-white hover:shadow-[#00f0ff]/40 active:scale-[0.98] transition-all"
            >
              <Plus size={14} strokeWidth={2.5} /> New board
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center gap-3 text-white/60"><Loader2 size={16} className="animate-spin" /> Loading boards…</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {boards.map((b) => {
              const Icon = VIEW_ICON[b.view] || Layers;
              return (
                <Link
                  key={b.slug}
                  to={`/admin/boards/${b.slug}`}
                  data-testid={`board-tile-${b.slug}`}
                  className="group relative rounded-2xl border border-white/10 bg-white/[0.03] p-6 hover:border-[#00f0ff]/30 hover:bg-white/[0.05] transition-all"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center border"
                      style={{ background: `${b.accent}12`, borderColor: `${b.accent}30`, color: b.accent }}
                    >
                      <Icon size={20} />
                    </div>
                    <div className="flex items-center gap-2">
                      {b.is_system && (
                        <span className="text-[9px] uppercase tracking-[0.24em] px-2 py-1 rounded-full bg-white/[0.05] text-white/40 border border-white/10">System</span>
                      )}
                      {!b.is_system && (
                        <button
                          onClick={(e) => { e.preventDefault(); del(b.slug, b.name); }}
                          data-testid={`board-delete-${b.slug}`}
                          className="w-8 h-8 rounded-full bg-white/[0.05] border border-white/10 hover:bg-[#ff0055]/15 hover:border-[#ff0055]/40 hover:text-[#ff0055] flex items-center justify-center text-white/50 transition-colors"
                          aria-label="Delete"
                        >
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="font-display font-bold text-xl mb-1.5 group-hover:text-[#00f0ff] transition-colors">
                    {b.name}
                  </div>
                  <div className="text-[#a395c3] text-sm mb-4 line-clamp-2 min-h-[40px]">{b.description}</div>
                  <div className="flex items-center gap-4 text-xs text-white/50">
                    <span className="inline-flex items-center gap-1.5"><Layers size={12} /> {b.card_count} cards</span>
                    <span className="inline-flex items-center gap-1.5">
                      {b.view === "kanban" ? <Kanban size={12} /> : <CalendarIcon size={12} />}
                      {b.view}
                    </span>
                    <span className="ml-auto text-white/40">{(b.columns || []).length} cols</span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      <NewBoardModal open={showNew} onClose={() => setShowNew(false)} onCreated={(b) => { setShowNew(false); load(); }} />
    </div>
  );
}
